import cn from 'classnames'
import {
  ELT_FORM_SECTION,
  IELTFormData,
  IELTLastMeetingData,
  TComplexSectionData,
} from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { DragEvent, memo, useCallback, useEffect, useState } from 'react'

import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Tooltip } from 'core-components/library/tooltip'
import { ConfirmDialog } from 'shared/components/confirmDialog'

import { EditableFields } from './components/editableFields/EditableFields'
import { ReadOnlyFields } from './components/readOnlyFields/readOnlyFields'
import { LAST_MEETING_EDIT_RECORD_FORM_KEY, LAST_MEETING_NEW_RECORD_FORM_KEY } from './configs'
import { getNewRecord } from './helpers/getNewRecord'
import { lastMeetingTranslation } from './translation'

import styles from './LastMeeting.module.scss'

interface IProps {
  formData: IELTFormData
  onChangeComplexFormData: (section: ELT_FORM_SECTION) => (data: TComplexSectionData) => void
}

export const LastMeeting = memo<IProps>(({ formData, onChangeComplexFormData }) => {
  const dispatch = useAppDispatch()
  const [isNewRecordVisible, setIsNewRecordVisible] = useState(false)
  const [editRecordId, setEditRecordId] = useState('')
  const [deleteRecordId, setDeleteRecordId] = useState('')
  const [draggedItem, setDraggedItem] = useState<IELTLastMeetingData | null>(null)
  const [lastMeetingData, setLastMeetingData] = useState<IELTLastMeetingData[]>(
    formData.LastMeetingData || []
  )
  const [newRecordData] = useGetFormData<IELTLastMeetingData>(LAST_MEETING_NEW_RECORD_FORM_KEY)
  const [editRecordData] = useGetFormData<IELTLastMeetingData>(LAST_MEETING_EDIT_RECORD_FORM_KEY)
  const isLastMeetingDataEmpty = lastMeetingData.length === 0
  const isSaveNewRecordDisabled = newRecordData.modifiedFields.length === 0
  const isSaveEditRecordDisabled = editRecordData.modifiedFields.length === 0

  /** --------------------------------------------- Helpers ------------------------------------------------ */

  const renderSaveRecordActions = useCallback(
    ({
      onSave,
      onCancel,
      isSaveDisabled,
    }: {
      onSave: () => void
      onCancel: () => void
      isSaveDisabled: boolean
    }) => (
      <>
        <Tooltip
          tipContent={() => <Translation span text={lastMeetingTranslation.rowActions.save} />}
        >
          <Button
            variant="text"
            color="success"
            disabled={isSaveDisabled}
            iconButton="kbase kbase-save"
            onClick={onSave}
          />
        </Tooltip>
        <Tooltip
          tipContent={() => <Translation span text={lastMeetingTranslation.rowActions.cancel} />}
        >
          <Button iconButton="kbase kbase-close" variant="text" color="danger" onClick={onCancel} />
        </Tooltip>
      </>
    ),
    []
  )

  /** ---------------------------------------- New record handlers ----------------------------------------- */

  const handleOnAddNewClick = useCallback(() => {
    setIsNewRecordVisible(true)
    setEditRecordId('')
    dispatch(resetAllModifiedDataByKey(LAST_MEETING_EDIT_RECORD_FORM_KEY))
  }, [dispatch])

  const handleOnSaveNewRecordClick = useCallback(() => {
    setIsNewRecordVisible(false)
    onChangeComplexFormData(ELT_FORM_SECTION.LAST_MEETING)([
      newRecordData.formData,
      ...lastMeetingData,
    ])
    dispatch(resetAllModifiedDataByKey(LAST_MEETING_NEW_RECORD_FORM_KEY))
  }, [dispatch, lastMeetingData, newRecordData.formData, onChangeComplexFormData])

  const handleOnCancelAddNewRecordClick = useCallback(() => {
    setIsNewRecordVisible(false)
    dispatch(resetAllModifiedDataByKey(LAST_MEETING_NEW_RECORD_FORM_KEY))
  }, [dispatch])

  /** ---------------------------------------- Record handlers ----------------------------------------- */

  const handleOnEditRecordClick = useCallback(
    (id: string) => {
      setIsNewRecordVisible(false)
      setEditRecordId(id)
      dispatch(resetAllModifiedDataByKey(LAST_MEETING_NEW_RECORD_FORM_KEY))
    },
    [dispatch]
  )

  const handleOnSaveEditRecordClick = useCallback(() => {
    if (editRecordId) {
      const newLastMeetingData = lastMeetingData.map((item) =>
        item.id === editRecordId ? { ...item, ...editRecordData.formData } : item
      )
      onChangeComplexFormData(ELT_FORM_SECTION.LAST_MEETING)(newLastMeetingData)
      setLastMeetingData(newLastMeetingData)
      setEditRecordId('')
    }
  }, [editRecordData.formData, editRecordId, lastMeetingData, onChangeComplexFormData])

  const handleOnCancelEditRecordClick = useCallback(() => {
    setEditRecordId('')
    dispatch(resetAllModifiedDataByKey(LAST_MEETING_EDIT_RECORD_FORM_KEY))
  }, [dispatch])

  const handleOnDeleteExistedRecordClick = useCallback(
    (id: string) => {
      setDeleteRecordId(id)
      dispatch(resetAllModifiedDataByKey(LAST_MEETING_EDIT_RECORD_FORM_KEY))
      setEditRecordId('')
    },
    [dispatch]
  )

  const handleOnDeleteExistedRecordConfirm = useCallback(() => {
    const newLastMeetingData = lastMeetingData.filter((item) => item.id !== deleteRecordId)
    onChangeComplexFormData(ELT_FORM_SECTION.LAST_MEETING)(newLastMeetingData)
    setLastMeetingData(newLastMeetingData)
    setDeleteRecordId('')
  }, [deleteRecordId, lastMeetingData, onChangeComplexFormData])

  const handleOnDeleteExistedRecordReject = useCallback(() => {
    setDeleteRecordId('')
  }, [])

  /** ---------------------------------------- Drag & Drop ----------------------------------------- */

  const handleDragStart = (e: any, index: number) => {
    setDraggedItem(lastMeetingData[index])
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'copyMove'
    }
  }

  const handleDragOver = (e: DragEvent<HTMLTableRowElement>, index: number) => {
    e.preventDefault()
    const draggedOverItem = lastMeetingData[index]

    // Ignore the dragged item
    if (draggedItem === draggedOverItem) return

    // Perform the swap
    const newData = [...lastMeetingData]
    const indexOfDraggedItem = newData.indexOf(draggedItem as IELTLastMeetingData)
    newData[indexOfDraggedItem] = draggedOverItem
    newData[index] = draggedItem as IELTLastMeetingData

    setLastMeetingData(newData)
  }

  const handleDrop = (e: DragEvent<HTMLTableRowElement>) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    setDraggedItem(null)
    onChangeComplexFormData(ELT_FORM_SECTION.LAST_MEETING)(lastMeetingData)
  }

  /** ---------------------------------------- Effects ----------------------------------------- */

  useEffect(() => {
    setLastMeetingData(formData.LastMeetingData)
  }, [formData.LastMeetingData])

  return (
    <div>
      <div className={styles.infoBlock}>
        <InfoBlock
          type={InfoBlockType.Help}
          text={lastMeetingTranslation.infoBlock.dragNDrop}
          hasIcon
        />
      </div>
      <div className={styles.mainActions}>
        <Button onClick={handleOnAddNewClick}>
          <Translation text={lastMeetingTranslation.mainActions.add} span />
        </Button>
      </div>
      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr>
            <th className={styles.statusCol}>
              <Translation text={lastMeetingTranslation.columnHeader.status} span />
            </th>
            <th className={styles.actionCol}>
              <Translation text={lastMeetingTranslation.columnHeader.action} span />
            </th>
            <th className={styles.updateCol}>
              <Translation text={lastMeetingTranslation.columnHeader.update} span />
            </th>
            <th />
          </tr>
        </thead>
        <tbody className={styles.tbody}>
          {isNewRecordVisible && (
            <tr className={styles.editable} key="new record">
              <EditableFields formKey={LAST_MEETING_NEW_RECORD_FORM_KEY} item={getNewRecord()} />
              <td style={{ verticalAlign: 'middle' }}>
                <div className={styles.rowActions}>
                  {renderSaveRecordActions({
                    onSave: handleOnSaveNewRecordClick,
                    onCancel: handleOnCancelAddNewRecordClick,
                    isSaveDisabled: isSaveNewRecordDisabled,
                  })}
                </div>
              </td>
            </tr>
          )}
          {lastMeetingData.map((item, index) => (
            <tr
              key={item.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={handleDrop}
              className={cn({ [styles.dragging]: Boolean(draggedItem) })}
            >
              {item.id === editRecordId ? (
                <EditableFields formKey={LAST_MEETING_EDIT_RECORD_FORM_KEY} item={item} />
              ) : (
                <ReadOnlyFields item={item} />
              )}
              <td style={{ verticalAlign: 'middle' }}>
                <div className={styles.rowActions}>
                  {item.id === editRecordId &&
                    renderSaveRecordActions({
                      onSave: handleOnSaveEditRecordClick,
                      onCancel: handleOnCancelEditRecordClick,
                      isSaveDisabled: isSaveEditRecordDisabled,
                    })}
                  {item.id !== editRecordId && (
                    <>
                      <Tooltip
                        tipContent={() => (
                          <Translation span text={lastMeetingTranslation.rowActions.edit} />
                        )}
                      >
                        <Button
                          variant="text"
                          color="grey"
                          iconButton="kbase kbase-edit"
                          onClick={() => {
                            handleOnEditRecordClick(item.id)
                          }}
                        />
                      </Tooltip>
                      <Tooltip
                        tipContent={() => (
                          <Translation span text={lastMeetingTranslation.rowActions.delete} />
                        )}
                      >
                        <Button
                          variant="text"
                          color="grey"
                          iconButton="kbase kbase-delete"
                          onClick={() => {
                            handleOnDeleteExistedRecordClick(item.id)
                          }}
                        />
                      </Tooltip>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
          {isLastMeetingDataEmpty && !isNewRecordVisible && (
            <tr style={{ height: '100%' }}>
              <td colSpan={4} style={{ position: 'relative', height: '100%' }}>
                <div className={styles.noData}>
                  <Translation span text={lastMeetingTranslation.common.noData} />
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <ConfirmDialog
        confirmBtnText={lastMeetingTranslation.confirmDeleteRecord.confirmBtn}
        rejectBtnText={lastMeetingTranslation.confirmDeleteRecord.rejectBtn}
        title={lastMeetingTranslation.confirmDeleteRecord.title}
        content={lastMeetingTranslation.confirmDeleteRecord.content}
        onConfirm={handleOnDeleteExistedRecordConfirm}
        onReject={handleOnDeleteExistedRecordReject}
        isVisible={Boolean(deleteRecordId)}
      />
    </div>
  )
})
